import type { OverlapConflict } from "@attendik/types/admin-schemas";

/**
 * Thrown for every non-2xx response and for network failures (status 0).
 * Carries the machine-readable `code` from the error envelope plus, for the
 * specific 409 cases, the structured payload the UI needs to render conflict
 * detail (overlap) or the in-progress sync id.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Populated on a 409 OVERLAP — the conflicting events. */
  conflicts?: OverlapConflict[];
  /** Populated on a 409 SYNC_IN_PROGRESS — the id of the running sync. */
  syncLogId?: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * A 403 from the API. The UI shows a clear "out of scope" message rather than
 * redirecting to login (the session is still valid — the resource is not).
 */
export class ScopeError extends ApiError {
  constructor(message = "You do not have access to this resource.") {
    super(403, "OUT_OF_SCOPE", message);
    this.name = "ScopeError";
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
